import { useState, useEffect, useCallback } from "react";
import { useSession } from "../App";
import { apiFetch } from "../lib/apiFetch";

const API = "/api/jaiman-memory";
const CATEGORIES = ["static", "episodic", "pattern", "conversation"];
const CATEGORY_LABELS = { static: "Static", episodic: "Episodic", pattern: "Pattern", conversation: "Conversation" };

const COLORS = {
  static:       { bg: "#e8f4fd", text: "#1565c0", border: "#90caf9" },
  episodic:     { bg: "#f3e5f5", text: "#6a1b9a", border: "#ce93d8" },
  pattern:      { bg: "#e8f5e9", text: "#2e7d32", border: "#a5d6a7" },
  conversation: { bg: "#fff8e1", text: "#e65100", border: "#ffcc02" },
};

function Badge({ label, style }) {
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 4,
      fontSize: 11, fontWeight: 600, lineHeight: "18px",
      ...style,
    }}>{label}</span>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div style={{
      background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10,
      padding: "16px 20px", minWidth: 110,
    }}>
      <div style={{ fontSize: 28, fontWeight: 700, color: "#111", lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

const TOPIC_LABELS = {
  code: "Electrical code",
  pricing: "Pricing",
  won_quote: "Won quotes",
  real_job: "Real jobs",
  quoting_format: "Quote format",
  company_facts: "Company facts",
};

// Below ~70% is the line where a topic needs a human look.
function accuracyColor(pct) {
  if (pct < 70) return { bar: "#dc2626", text: "#991b1b" };
  if (pct < 85) return { bar: "#f59e0b", text: "#92400e" };
  return { bar: "#16a34a", text: "#166534" };
}

function TrainingPanel({ report, days, onDays, loading, error }) {
  const [showMisses, setShowMisses] = useState(false);

  const topics = Object.entries(report?.by_topic || {})
    .sort((a, b) => (a[1].accuracy || 0) - (b[1].accuracy || 0));
  const misses = report?.recent_misses || [];

  return (
    <div style={{
      background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10,
      padding: "14px 20px", marginBottom: 20,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
          Training accuracy
          {report?.total_questions != null && (
            <span style={{ fontWeight: 400, color: "#9ca3af", marginLeft: 8 }}>
              {report.total_questions} questions · last {days}d
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {[7, 14, 30].map(d => (
            <button
              key={d}
              onClick={() => onDays(d)}
              style={{
                border: "1px solid #e5e7eb", borderRadius: 6, cursor: "pointer",
                padding: "3px 10px", fontSize: 12, fontWeight: 600,
                background: d === days ? "#111" : "#fff",
                color: d === days ? "#fff" : "#6b7280",
              }}
            >{d}d</button>
          ))}
        </div>
      </div>

      {loading && <div style={{ fontSize: 13, color: "#9ca3af" }}>Loading training data…</div>}
      {error && <div style={{ fontSize: 13, color: "#991b1b" }}>Couldn't load training data: {error}</div>}
      {!loading && !error && !topics.length && (
        <div style={{ fontSize: 13, color: "#9ca3af" }}>No drills logged in this window yet.</div>
      )}

      {topics.map(([topic, t]) => {
        const pct = Math.round(t.accuracy || 0);
        const c = accuracyColor(pct);
        return (
          <div key={topic} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <div style={{ width: 118, fontSize: 12, color: "#374151", flexShrink: 0 }}>
              {TOPIC_LABELS[topic] || topic}
            </div>
            <div style={{ flex: 1, height: 8, background: "#f3f4f6", borderRadius: 4, overflow: "hidden" }}>
              <div style={{ width: `${pct}%`, height: "100%", background: c.bar, borderRadius: 4 }} />
            </div>
            <div style={{ width: 38, fontSize: 12, fontWeight: 700, color: c.text, textAlign: "right" }}>{pct}%</div>
            <div style={{ width: 92, fontSize: 11, color: "#9ca3af", textAlign: "right" }}>
              {t.correct}/{t.total} · {t.partial} partial
            </div>
          </div>
        );
      })}

      {misses.length > 0 && (
        <div style={{ marginTop: 12, borderTop: "1px solid #f3f4f6", paddingTop: 10 }}>
          <button
            onClick={() => setShowMisses(v => !v)}
            style={{
              background: "none", border: "none", padding: 0, cursor: "pointer",
              fontSize: 12, fontWeight: 600, color: "#1565c0",
            }}
          >
            {showMisses ? "▾" : "▸"} Recent misses ({misses.length})
          </button>
          {showMisses && misses.map((m, i) => (
            <div key={i} style={{
              marginTop: 8, padding: "8px 10px", background: "#fafafa",
              border: "1px solid #f3f4f6", borderRadius: 6,
            }}>
              <Badge label={TOPIC_LABELS[m.topic] || m.topic} style={{ background: "#fee2e2", color: "#991b1b", border: "1px solid #fecaca" }} />
              <div style={{ fontSize: 12, color: "#374151", marginTop: 6, fontWeight: 500 }}>{m.q}</div>
              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4, lineHeight: 1.5 }}>{m.why}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function startOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function startOfThisWeek() {
  const d = startOfToday();
  const day = d.getDay(); // Sun=0
  const diff = (day === 0 ? 6 : day - 1); // start Mon
  d.setDate(d.getDate() - diff);
  return d;
}

function groupByPeriod(memories) {
  const today = startOfToday();
  const weekStart = startOfThisWeek();
  const groups = { today: [], thisWeek: [], earlier: [] };
  for (const m of memories) {
    const created = new Date(m.created_at);
    if (created >= today) groups.today.push(m);
    else if (created >= weekStart) groups.thisWeek.push(m);
    else groups.earlier.push(m);
  }
  return groups;
}

export default function JaiManLearning() {
  const { session } = useSession() || {};
  const userEmail = session?.user?.email?.toLowerCase() || "";

  const [memories, setMemories] = useState([]);
  const [stats, setStats] = useState(null);
  const [topRef, setTopRef] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [page, setPage] = useState(1);

  // Custom date range picker for bulk delete
  const [rangeOpen, setRangeOpen] = useState(false);
  const [rangeFrom, setRangeFrom] = useState("");
  const [rangeTo, setRangeTo] = useState("");

  // Read filters from URL query params on mount (deep-link from Meeting Notes toast etc.)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const t = params.get("tag");
    const d = params.get("date");
    const tags = [t, d ? `date:${d}` : null].filter(Boolean);
    if (tags.length) setTagFilter(tags.join(","));
  }, []);

  // Tier-1 trainer report (api/jaiman-trainer.js ?action=report)
  const [training, setTraining] = useState(null);
  const [trainingDays, setTrainingDays] = useState(14);
  const [trainingLoading, setTrainingLoading] = useState(false);
  const [trainingError, setTrainingError] = useState(null);

  useEffect(() => {
    if (!userEmail) return undefined;
    let cancelled = false;
    setTrainingLoading(true);
    setTrainingError(null);
    apiFetch(`/api/jaiman-trainer?action=report&userEmail=${encodeURIComponent(userEmail)}&days=${trainingDays}`)
      .then(r => r.json())
      .then(d => {
        if (cancelled) return;
        if (d?.error) setTrainingError(d.error);
        else setTraining(d);
      })
      .catch(e => { if (!cancelled) setTrainingError(e.message); })
      .finally(() => { if (!cancelled) setTrainingLoading(false); });
    return () => { cancelled = true; };
  }, [userEmail, trainingDays]);

  const [actionLoading, setActionLoading] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  };

  const load = useCallback(async () => {
    if (!userEmail) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ userEmail, page, limit: 50 });
      if (search) params.set("search", search);
      if (categoryFilter) params.set("category", categoryFilter);
      if (tagFilter) params.set("tag", tagFilter);
      const res = await apiFetch(`${API}?${params}`);
      if (!res.ok) throw new Error((await res.json())?.error || "Failed to load");
      const data = await res.json();
      setMemories(data.memories || []);
      setStats(data.stats || null);
      setTopRef(data.top_referenced || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userEmail, page, search, categoryFilter, tagFilter]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this memory? This cannot be undone.")) return;
    setActionLoading(id + ":delete");
    try {
      const res = await apiFetch(API, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userEmail, id }),
      });
      if (!res.ok) throw new Error((await res.json())?.error || "Failed");
      showToast("Memory deleted");
      load();
    } catch (err) { showToast(err.message, false); }
    finally { setActionLoading(null); }
  };

  const handleBulkDelete = async (from, to, label) => {
    if (!window.confirm(`Delete all memories ${label}? This cannot be undone.`)) return;
    setActionLoading("bulk");
    try {
      const res = await apiFetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "bulkDelete", userEmail, from, to }),
      });
      if (!res.ok) throw new Error((await res.json())?.error || "Failed");
      const data = await res.json();
      showToast(`Deleted ${data.count} memor${data.count === 1 ? "y" : "ies"}`);
      setRangeOpen(false);
      setRangeFrom("");
      setRangeTo("");
      load();
    } catch (err) { showToast(err.message, false); }
    finally { setActionLoading(null); }
  };

  const quickDelete = (windowMs, label) => {
    const to = new Date();
    const from = new Date(to.getTime() - windowMs);
    handleBulkDelete(from.toISOString(), to.toISOString(), label);
  };

  const deleteToday = () => {
    const from = startOfToday();
    handleBulkDelete(from.toISOString(), new Date().toISOString(), "from today");
  };

  const deleteCustomRange = () => {
    if (!rangeFrom && !rangeTo) {
      showToast("Pick a from or to date", false);
      return;
    }
    const fromIso = rangeFrom ? new Date(rangeFrom + "T00:00:00").toISOString() : null;
    const toIso = rangeTo ? new Date(rangeTo + "T23:59:59").toISOString() : null;
    const label = `between ${rangeFrom || "start"} and ${rangeTo || "now"}`;
    handleBulkDelete(fromIso, toIso, label);
  };

  const groups = groupByPeriod(memories);

  const renderMemory = (m) => {
    const catColor = COLORS[m.category] || {};
    return (
      <div key={m.id} style={{
        background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10,
        padding: "12px 16px", marginBottom: 8,
        borderLeft: `3px solid ${catColor.border || "#e5e7eb"}`,
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
          <Badge
            label={CATEGORY_LABELS[m.category] || m.category}
            style={{ background: catColor.bg, color: catColor.text, border: `1px solid ${catColor.border}` }}
          />
          {m.reference_count > 0 && (
            <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: "auto" }}>{m.reference_count}× referenced</span>
          )}
        </div>

        <div style={{ fontSize: 14, color: "#111827", lineHeight: 1.5, marginBottom: 6 }}>{m.content}</div>

        {m.context && (
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>
            Context: {m.context}
          </div>
        )}

        {m.tags?.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 8 }}>
            {m.tags.map(tag => (
              <span key={tag} style={{
                background: "#f3f4f6", color: "#374151",
                padding: "2px 6px", borderRadius: 4, fontSize: 11,
              }}>#{tag}</span>
            ))}
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 11, color: "#9ca3af" }}>
            {m.created_by === "system" ? "system" : m.created_by} · {new Date(m.created_at).toLocaleString()}
          </span>
          <button
            onClick={() => handleDelete(m.id)}
            disabled={!!actionLoading}
            style={{
              marginLeft: "auto",
              padding: "4px 10px", borderRadius: 6, border: "1px solid #fca5a5",
              background: "#fee2e2", color: "#991b1b", fontSize: 12, cursor: "pointer",
            }}
          >
            {actionLoading === m.id + ":delete" ? "..." : "Delete"}
          </button>
        </div>
      </div>
    );
  };

  const renderGroup = (title, items) => {
    if (items.length === 0) return null;
    return (
      <div style={{ marginBottom: 20 }}>
        <div style={{
          fontSize: 12, fontWeight: 600, color: "#6b7280",
          textTransform: "uppercase", letterSpacing: "0.5px",
          marginBottom: 8, paddingBottom: 6, borderBottom: "1px solid #e5e7eb",
        }}>
          {title} <span style={{ color: "#9ca3af", fontWeight: 400 }}>({items.length})</span>
        </div>
        {items.map(renderMemory)}
      </div>
    );
  };

  return (
    <div style={{ padding: "20px 16px", maxWidth: 900, margin: "0 auto", fontFamily: "'Roboto', sans-serif" }}>

      {toast && (
        <div style={{
          position: "fixed", top: 16, right: 16, zIndex: 9999,
          background: toast.ok ? "#065f46" : "#991b1b", color: "#fff",
          padding: "10px 18px", borderRadius: 8, fontSize: 14, fontWeight: 500,
          boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
        }}>{toast.msg}</div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
        <div style={{
          width: 40, height: 40, borderRadius: "50%",
          background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 20,
        }}>🧠</div>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#111" }}>Jai-Man Learning</div>
          <div style={{ fontSize: 13, color: "#6b7280" }}>Activity log of everything Jai-Man has learned</div>
        </div>
      </div>

      {/* Stats row */}
      {stats && (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
          <StatCard label="Total Memories" value={stats.total} />
          <StatCard label="Today" value={groups.today.length} sub="new memories" />
          <StatCard label="This Week" value={groups.today.length + groups.thisWeek.length} />
        </div>
      )}

      {/* Tier-1 training accuracy */}
      <TrainingPanel
        report={training}
        days={trainingDays}
        onDays={setTrainingDays}
        loading={trainingLoading}
        error={trainingError}
      />

      {/* Bulk delete controls */}
      <div style={{
        background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10,
        padding: "14px 20px", marginBottom: 20,
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 10 }}>
          🗑️ Bulk delete
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <button
            onClick={() => quickDelete(60 * 60 * 1000, "from the last hour")}
            disabled={actionLoading === "bulk"}
            style={bulkBtnStyle}
          >
            Last hour
          </button>
          <button
            onClick={deleteToday}
            disabled={actionLoading === "bulk"}
            style={bulkBtnStyle}
          >
            Today
          </button>
          <button
            onClick={() => quickDelete(7 * 24 * 60 * 60 * 1000, "from the last 7 days")}
            disabled={actionLoading === "bulk"}
            style={bulkBtnStyle}
          >
            Last 7 days
          </button>
          <button
            onClick={() => quickDelete(30 * 24 * 60 * 60 * 1000, "from the last 30 days")}
            disabled={actionLoading === "bulk"}
            style={bulkBtnStyle}
          >
            Last 30 days
          </button>
          <button
            onClick={() => setRangeOpen(o => !o)}
            disabled={actionLoading === "bulk"}
            style={{ ...bulkBtnStyle, background: rangeOpen ? "#dbeafe" : "#fff", color: rangeOpen ? "#1e40af" : "#374151" }}
          >
            Custom range…
          </button>
          {actionLoading === "bulk" && (
            <span style={{ fontSize: 12, color: "#6b7280", marginLeft: 8 }}>Deleting…</span>
          )}
        </div>
        {rangeOpen && (
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
            <label style={{ fontSize: 12, color: "#6b7280" }}>From</label>
            <input
              type="date"
              value={rangeFrom}
              onChange={e => setRangeFrom(e.target.value)}
              style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 13 }}
            />
            <label style={{ fontSize: 12, color: "#6b7280" }}>To</label>
            <input
              type="date"
              value={rangeTo}
              onChange={e => setRangeTo(e.target.value)}
              style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 13 }}
            />
            <button
              onClick={deleteCustomRange}
              disabled={actionLoading === "bulk"}
              style={{
                padding: "6px 14px", borderRadius: 6, border: "1px solid #991b1b",
                background: "#991b1b", color: "#fff", fontSize: 13, cursor: "pointer", fontWeight: 500,
              }}
            >
              Delete range
            </button>
          </div>
        )}
      </div>

      {/* Top referenced */}
      {topRef.length > 0 && (
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: "16px 20px", marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#374151", marginBottom: 12 }}>Most Referenced</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {topRef.map(m => (
              <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}>
                <span style={{ color: "#6b7280", minWidth: 28, textAlign: "right" }}>{m.reference_count}×</span>
                <Badge
                  label={CATEGORY_LABELS[m.category] || m.category}
                  style={{ background: COLORS[m.category]?.bg, color: COLORS[m.category]?.text, border: `1px solid ${COLORS[m.category]?.border}` }}
                />
                <span style={{ color: "#374151" }}>{m.content}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Category breakdown */}
      {stats?.by_category && Object.keys(stats.by_category).length > 0 && (
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: "14px 20px", marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#374151", marginBottom: 10 }}>By Category</div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => { setCategoryFilter(categoryFilter === cat ? "" : cat); setPage(1); }}
                style={{
                  padding: "4px 12px", borderRadius: 20, border: "none", cursor: "pointer", fontSize: 13,
                  background: categoryFilter === cat ? COLORS[cat]?.bg : "#f3f4f6",
                  color: categoryFilter === cat ? COLORS[cat]?.text : "#374151",
                  fontWeight: categoryFilter === cat ? 600 : 400,
                  transition: "all 0.15s",
                }}
              >
                {CATEGORY_LABELS[cat]}: {stats.by_category[cat] || 0}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Search & filters */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        <input
          type="text"
          placeholder="Search memories..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          style={{
            flex: "1 1 200px", padding: "8px 12px", borderRadius: 8,
            border: "1px solid #d1d5db", fontSize: 14, outline: "none",
            background: "#fff",
          }}
        />
        <button
          onClick={() => { setTagFilter(tagFilter.includes("meeting-notes") ? "" : "meeting-notes"); setPage(1); }}
          style={{
            padding: "8px 14px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 13,
            background: tagFilter.includes("meeting-notes") ? "#dbeafe" : "#fff",
            color: tagFilter.includes("meeting-notes") ? "#1e40af" : "#374151",
            fontWeight: tagFilter.includes("meeting-notes") ? 600 : 400,
            cursor: "pointer",
          }}
        >
          📎 Meeting Notes
        </button>
        {(search || categoryFilter || tagFilter) && (
          <button
            onClick={() => { setSearch(""); setCategoryFilter(""); setTagFilter(""); setPage(1); }}
            style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 13, background: "#fff", cursor: "pointer", color: "#6b7280" }}
          >
            Clear
          </button>
        )}
      </div>

      {tagFilter && (
        <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 12 }}>
          Filtering by tag{tagFilter.includes(",") ? "s" : ""}: <strong style={{ color: "#374151" }}>{tagFilter}</strong>
        </div>
      )}

      {/* Memory log */}
      {loading && (
        <div style={{ textAlign: "center", color: "#6b7280", padding: "40px 0", fontSize: 14 }}>Loading memories...</div>
      )}
      {error && (
        <div style={{ background: "#fee2e2", color: "#991b1b", padding: "12px 16px", borderRadius: 8, fontSize: 14, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {!loading && memories.length === 0 && (
        <div style={{ textAlign: "center", color: "#9ca3af", padding: "60px 0", fontSize: 14 }}>
          No memories yet — Jai-Man will save things as it learns them.
        </div>
      )}

      {!loading && memories.length > 0 && (
        <>
          {renderGroup("Today", groups.today)}
          {renderGroup("This week", groups.thisWeek)}
          {renderGroup("Earlier", groups.earlier)}
        </>
      )}

      {/* Pagination */}
      {memories.length === 50 && (
        <div style={{ display: "flex", justifyContent: "center", gap: 12, marginTop: 20 }}>
          {page > 1 && (
            <button
              onClick={() => setPage(p => p - 1)}
              style={{ padding: "8px 20px", borderRadius: 8, border: "1px solid #d1d5db", background: "#fff", cursor: "pointer", fontSize: 14 }}
            >
              ← Previous
            </button>
          )}
          <button
            onClick={() => setPage(p => p + 1)}
            style={{ padding: "8px 20px", borderRadius: 8, border: "1px solid #d1d5db", background: "#fff", cursor: "pointer", fontSize: 14 }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

const bulkBtnStyle = {
  padding: "6px 14px", borderRadius: 6, border: "1px solid #d1d5db",
  background: "#fff", color: "#374151", fontSize: 13, cursor: "pointer",
};
